const resolveWeight = (row, weightAccessor) => {
  if (typeof weightAccessor !== "function") return 1;
  const weight = Number(weightAccessor(row));
  if (!Number.isFinite(weight)) return 0;
  return Math.max(0, weight);
};

const computeWeightedMedian = (sortedPoints, totalWeight) => {
  if (!sortedPoints.length || totalWeight <= 0) return 0;
  const midpoint = totalWeight / 2;
  let cumulative = 0;

  for (let index = 0; index < sortedPoints.length; index += 1) {
    const current = sortedPoints[index];
    cumulative += current.weight;
    if (cumulative < midpoint) continue;

    if (Math.abs(cumulative - midpoint) < 1e-9) {
      const next = sortedPoints[index + 1];
      return next ? (current.value + next.value) / 2 : current.value;
    }
    return current.value;
  }

  return sortedPoints[sortedPoints.length - 1].value;
};

export const computeStatistics = (rows, field, options = {}) => {
  const weightAccessor = options?.weightAccessor;
  const points = rows
    .map((row) => ({
      value: Number(row[field]),
      weight: resolveWeight(row, weightAccessor),
    }))
    .filter((point) => !Number.isNaN(point.value) && point.value !== -99 && point.weight > 0);
  if (!points.length) return { average: 0, median: 0, stdDev: 0, sampleSize: 0, min: 0, max: 0 };

  const sampleSize = points.reduce((sum, point) => sum + point.weight, 0);
  if (sampleSize <= 0) {
    return { average: 0, median: 0, stdDev: 0, sampleSize: 0, min: 0, max: 0 };
  }

  const weightedSum = points.reduce((sum, point) => sum + point.value * point.weight, 0);
  const average = weightedSum / sampleSize;
  const sortedPoints = [...points].sort((a, b) => a.value - b.value);
  const median = computeWeightedMedian(sortedPoints, sampleSize);
  const variance =
    points.reduce((sum, point) => sum + point.weight * (point.value - average) ** 2, 0) /
    sampleSize;
  const stdDev = Math.sqrt(variance);

  return {
    average,
    median,
    stdDev: Number(stdDev.toFixed(1)),
    sampleSize,
    min: sortedPoints[0].value,
    max: sortedPoints[sortedPoints.length - 1].value,
  };
};

export const getAllCodes = (mapping) =>
  Object.keys(mapping)
    .map(Number)
    .filter((c) => c !== -99)
    .sort((a, b) => a - b);

export const gaussian = (x, mu, sigma) =>
  (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mu) ** 2) / (2 * sigma ** 2));

export const buildGaussianSeries = (mu, sigma, min, max, n = 60) => {
  if (!sigma || !isFinite(sigma)) return [];
  const step = (max - min) / n;
  const peak = gaussian(mu, mu, sigma);
  return Array.from({ length: n + 1 }, (_, i) => {
    const x = min + i * step;
    return { x, y: gaussian(x, mu, sigma) / peak };
  });
};
